import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './SuspiciousLinkChecker.css'
import { checkLink } from './utils/linkCheckApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const isValidUrl = (value) => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const RISK_LABELS = {
  baixo: 'Risco baixo',
  medio: 'Risco médio',
  alto: 'Risco alto',
}

const SuspiciousLinkChecker = () => {
  useDocumentMeta({
    title: 'Verificador de Link Suspeito | jarder.dev',
    description:
      'Cole um link recebido por SMS ou WhatsApp e confira reputação (Google ' +
      'Safe Browsing), idade do domínio e certificado de segurança. Grátis.',
  })

  const [input, setInput] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('idle')

  const check = async () => {
    if (!isValidUrl(input)) {
      setError('Cole uma URL válida, começando com http:// ou https://.')
      setResult(null)
      return
    }
    setError('')
    setStatus('loading')
    try {
      const data = await checkLink(input)
      setResult(data)
      trackEvent('tool_generate', { tool: 'link_checker', risk: data.riskLevel })
    } catch (err) {
      setError(err.message || 'Não foi possível verificar esse link agora. Tente novamente.')
      setResult(null)
    } finally {
      setStatus('idle')
    }
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Verificador de Link Suspeito</h1>
      <p className="tool-disclaimer">
        Recebeu um link por SMS ou WhatsApp (falso Correios, falso Pix,
        falso banco)? Cole aqui antes de clicar. Conferimos reputação
        (Google Safe Browsing), idade do domínio e certificado de
        segurança — nenhuma checagem sozinha é garantia, mas juntas dão
        um bom sinal de alerta.
      </p>

      <div className="tool-field">
        <label htmlFor="suspicious-url">Link a verificar</label>
        <input
          id="suspicious-url"
          type="text"
          placeholder="https://exemplo.com/rastreamento"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="tool-button"
        onClick={check}
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Verificando...' : 'Verificar'}
      </button>

      {error && <p className="tool-error">{error}</p>}

      {result && (
        <div className="link-check-result" style={{ marginTop: 20 }}>
          <span className={`link-check-badge link-check-badge-${result.riskLevel}`}>
            {RISK_LABELS[result.riskLevel]}
          </span>

          <ul className="link-check-reasons">
            {result.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>

          <div className="link-check-details">
            <div className="link-check-detail">
              <span className="link-check-detail-label">Google Safe Browsing</span>
              <span>
                {result.safeBrowsing.checked
                  ? result.safeBrowsing.flagged
                    ? 'Listado como perigoso'
                    : 'Nenhum registro encontrado'
                  : 'Não foi possível checar agora'}
              </span>
            </div>
            <div className="link-check-detail">
              <span className="link-check-detail-label">Domínio</span>
              <span>
                {result.domainAge.ageDays !== null
                  ? `${result.domainAge.domain} — registrado há ${result.domainAge.ageDays} dia(s)`
                  : `${result.domainAge.domain} — idade não disponível`}
              </span>
            </div>
            <div className="link-check-detail">
              <span className="link-check-detail-label">Certificado (HTTPS)</span>
              <span>
                {!result.certificate
                  ? 'Site não usa HTTPS'
                  : result.certificate.valid
                    ? `Válido, emitido por ${result.certificate.issuer || 'desconhecido'}`
                    : 'Inválido ou não verificado'}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="tool-info-section">
        <h2>Como funciona</h2>
        <p>
          Reputação: conferimos o link contra a lista de sites de
          phishing/malware do Google Safe Browsing. Idade do domínio:
          consultamos o registro público (RDAP) — domínios criados há
          poucos dias são um sinal clássico de golpe recém-armado.
          Certificado: verificamos se o site usa HTTPS e há quanto tempo o
          certificado foi emitido.
        </p>
        <p>
          Nenhuma dessas checagens sozinha prova que um site é seguro ou
          perigoso — use como mais um sinal de alerta, não como veredito
          final.
        </p>
      </div>
    </div>
  )
}

export default SuspiciousLinkChecker
